import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import moment from "moment";

import { createAxiosInstance } from "api/axiosConfig";
import { CDM_DEFAULT_CORRECTION_REASON } from "features/cdm/constants/correctionReasonOptions";
import { useSyncPatientBaselineFromReview } from "features/cdm/hooks/useSyncPatientBaselineFromReview";
import { toReviewLocalDate } from "features/cdm/utils/reviewDate";
import { toNumberOrUndefined } from "features/cdm/utils/toNumberOrUndefined";

export const useSendCDM = ({ patientId }) => {
  const queryClient = useQueryClient();
  const { syncBaseline } = useSyncPatientBaselineFromReview({ patientId });

  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    // Callers surface the real BE message via extractSubmissionError; suppress
    // the generic global mutation toast. 403 is still handled globally.
    meta: { skipGlobalError: true },
    mutationFn: async (data) => {
      const axiosInstance = createAxiosInstance();
      // Per-factor CHA2DS2-VASc breakdown and QRisk3 conditions are persisted
      // opaque (clinical_data JSONB) so they carry forward cross-device/session
      // (KIN-1261). Send null when nothing was recorded.
      const cha2ds2vascFactors =
        data.cha2ds2vascFactors &&
        Object.keys(data.cha2ds2vascFactors).length > 0
          ? data.cha2ds2vascFactors
          : null;
      const qRisk3Conditions =
        data.qRisk3Conditions && Object.keys(data.qRisk3Conditions).length > 0
          ? data.qRisk3Conditions
          : null;
      try {
        const response = await axiosInstance.put(
          `${tenantVal}/cdm/patients/${patientId}/cdm/submit`,
          {
            reviewType: data.reviewType,
            reviewDate: toReviewLocalDate(data.reviewDate),
            originalReimbursementMessageControlId:
              data?.reimbursementMessageControlId || null,
            // Reason for Correction (X0339-1) — only on a correction (CDM has a
            // single allowed value); BE emits the X0339 OBX in the CDR message.
            correctionReason: data?.reimbursementMessageControlId
              ? CDM_DEFAULT_CORRECTION_REASON
              : null,
            clinicalData: {
              diagnosis: {
                diagnoses: data.registrationReasons,
                yearOfDiagnosis: data.yearOfDiagnosis,
                attendingHospital: data.attendingHospital,
              },
              emergencyAdmissions: {
                edAttendancesLast6Months: data.edAttendancesLast6Months,
                unscheduledAdmissionsLast6Months:
                  data.unscheduledAdmissionsLast6Months,
              },
              medicationReview: data.medicationReview === "yes" ? true : false,
              riskFactors: {
                smokingStatus: data.smokingStatus,
                vapingStatus: data.vapingStatus,
                weightKg: Number(data.weight),
                heightCm: Number(data.height),
                bmi: Number(data.bmi),
                waistCircumferenceCm: Number(data.waistCircumference),
                qrisk3Score: data.qRisk3Score != null ? Number(data.qRisk3Score) : 0,
                qRisk3Conditions,
                alcoholAuditCScore: Number(data.alcoholRiskScore),
                alcoholFullAuditScore: data.alcoholFullAuditScore ? Number(data.alcoholFullAuditScore) : null,
                physicalActivityQ1: data.physicalActivityQ1,
                physicalActivityQ2: data.physicalActivityQ2,
                smokingInterventions: (data.smokingInterventions || []).filter(
                  Boolean
                ),
                smokingInterventionOther:
                  data.smokingInterventions?.includes("OTHER")
                    ? data.smokingInterventionOther || null
                    : null,
                alcoholDependenceInterventions: (
                  data.alcoholDependenceInterventions || []
                ).filter(Boolean),
                alcoholHigherRiskInterventions: (
                  data.alcoholHigherRiskInterventions || []
                ).filter(Boolean),
                alcoholIncreasingRiskInterventions: (
                  data.alcoholIncreasingRiskInterventions || []
                ).filter(Boolean),
                weightHighRiskInterventions: (
                  data.weightHighRiskInterventions || []
                ).filter(Boolean),
                weightIncreasedRiskInterventions: (
                  data.weightIncreasedRiskInterventions || []
                ).filter(Boolean),
                weightNormalInterventions: (
                  data.weightNormalInterventions || []
                ).filter(Boolean),
                physicalActivityInterventions: (
                  data.physicalActivityInterventions || []
                ).filter(Boolean),

                influenzaVaccine: data.influenzaVaccine,
                influenzaVaccineDate: data.influenzaVaccineDate
                  ? moment(new Date(dayjs(data.influenzaVaccineDate, "DD-MM-YYYY"))).format("YYYY-MM-DD")
                  : null,
                pneumococcalVaccine: data.pneumococcalVaccine,
                pneumococcalVaccineDate: data.pneumococcalVaccineDate
                  ? moment(new Date(dayjs(data.pneumococcalVaccineDate, "DD-MM-YYYY"))).format("YYYY-MM-DD")
                  : null,
                covidVaccine: data.covidVaccine,
                covidVaccineStatusUpToDate:
                  data.covidVaccineStatusUpToDate === "YES" ? true : false,
              },
              physicalExam: {
                pulseRate: Number(data.pulse),
                pulseRhythm: data.pulseRhythm,
                systolicBloodPressure: Number(data.bp1),
                diastolicBloodPressure: Number(data.bp2),
              },
              diabetesSpecifics: {
                diabetesRelatedAmputation:
                  data.diabetesRelatedAmputation === "YES" ? true : false,
                amputationYear: Number(data.amputationYear),
                footAndLimbReviewFindings: Array.isArray(data?.footAndLimbReviewFindings)
                  ? data.footAndLimbReviewFindings.filter(Boolean)
                  : [],
                footAndLimbReviewComment: data.footAndLimbReviewComment || null,
                retinaScreeningUpToDate:
                  data.retinaScreeningUpToDate === "YES" ? true : false,
                referredForRetinaScreening: data.referredForRetinaScreening || null,
              },
              asthmaCopdSpecifics: {
                exacerbationsLast6Months: data.exacerbationsLast6Months,
              },
              diagnosticInvestigations: {
                ecgSinceLastReview:
                  data.ecgSinceLastReview === "YES" ? true : false,
                ecgResult: data.ecgResult || null,
                echoSinceLastReview:
                  data.echoSinceLastReview === "YES" ? true : false,
                echoResult: data.echoResult || null,

                spirometrySinceLastReview:
                  data.spirometrySinceLastReview === "YES" ? true : false,
                spirometryResult: data.spirometryResult || null,
              },
              laboratoryInvestigations: {
                haemoglobin: toNumberOrUndefined(data.haemoglobin),
                totalCholesterol: data.totalCholesterol === "NOT_CLINICALLY_NECESSARY" ? "NOT_CLINICALLY_NECESSARY" : toNumberOrUndefined(data.totalCholesterol),
                hdlCholesterol: data.hdlCholesterol === "NOT_CLINICALLY_NECESSARY" ? "NOT_CLINICALLY_NECESSARY" : toNumberOrUndefined(data.hdlCholesterol),
                nonHdlCholesterol: toNumberOrUndefined(data.nonHdlCholesterol),
                ldlCholesterol: data.ldlCholesterol,
                triglycerides: data.triglycerides === "NOT_CLINICALLY_NECESSARY" ? "NOT_CLINICALLY_NECESSARY" : toNumberOrUndefined(data.triglycerides),
                hba1c: toNumberOrUndefined(data.hba1c),
                serumCreatinine: toNumberOrUndefined(data.serumCreatinine),
                egfr: data.egfr,
                tftConducted: data.tftConducted === "YES" ? true : false,
                lftConducted: data.lftConducted === "YES" ? true : false,

                fastingGlucose: data.fastingGlucose,
                creatinineClearance: toNumberOrUndefined(data.creatinineClearance),
                ntProBNP: data.ntProBNP,
                acr: data.acr === "Not Available" ? "Not Available" : Number(data.acr) || null,
              },
              diseaseAssessmentScores: {
                copdDyspnoeaScore: Number(data.copdDyspnoeaScore),
                cha2ds2vascScore: Number(data.cha2ds2vascScore),
                cha2ds2vascFactors,
              },
              patientEducation: {
                educationProvided: data.educationProvided || null,
                referredToEducation: data.referredToEducation || null,
                referredToDiabetesPrevention: data.referredToDiabetesPrevention || "NOT_AVAILABLE",
                referralDetails: data.referralDetails || null,
                agreedWrittenCarePlan: data.agreedWrittenCarePlan || null,
              },
              patientDemographics: {
                ethnicityCode: data.ethnicityCode,
              },
              otherMajorDiagnoses: {
                diagnoses: data.otherMajorDiagnoses?.diagnoses ?? [],
                otherText: data.otherMajorDiagnoses?.otherText || null,
              },
              carePlan: data.carePlan,
            },
          }
        );

        // KIN-1236: a mid-month registration / GP panel transfer review is held
        // by the BE as a DRAFT (HL7 not sent) and returned as 202 with
        // holdUntil/holdReason. Surface a `held` marker so the caller shows the
        // held-draft state instead of "sent". Inert until the BE hold ships.
        const held =
          response.status === 202 ||
          response.data?.reimbursementMessageStatus === "DRAFT";

        return { ...response.data, held };
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patientCDM"] });
      queryClient.invalidateQueries({ queryKey: ["patientCDMStatus"] });
      queryClient.invalidateQueries({ queryKey: ["patient-cdm"] });
      syncBaseline(variables);
    },
  });
};
